import React, { useEffect, useRef, useState, useMemo } from "react";
import { AiFillEye, AiFillGithub } from "react-icons/ai";
import { BsHandIndex } from "react-icons/bs";
import { Work, worksList } from "./work";
import { useTranslation, Trans } from "react-i18next";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import "./Works.scss";

gsap.registerPlugin(ScrollTrigger);

const Works = () => {
  const title = useRef(null);
  const container = useRef(null);
  const buttons = useRef('.works__filter button');

  useGSAP(() => {
    gsap.set(title.current, {xPercent: -100, opacity: 0})
    gsap.to(title.current, {
      scrollTrigger: title.current,
      opacity: 1, 
      xPercent: 0, 
      duration: 1, 
      ease: "slow",
    });
    gsap.fromTo(buttons.current, {
      opacity: 0
    }, {
      scrollTrigger: buttons.current,
      opacity: 1,
      duration: 1, 
      ease: "power1.inOu",
      stagger: 0.2,
      delay: 0.5
    });
    gsap.fromTo('.works__scroll svg', {
      xPercent: 100,
    }, {
      scrollTrigger: '.works__scroll svg',
      xPercent: 0,
      duration: 2,
      repeat: -1
    });
  }, { scope: container });


  const {t, i18n} = useTranslation();
  const all = t('works_all');
  let currentWorks: Work[] = worksList.en;
  currentWorks = useMemo(() => {
    for (const [key, value] of Object.entries(worksList)) {
      if (key === i18n.resolvedLanguage) {
        return value;
      }
    }
  }, [i18n.resolvedLanguage]);

  const [activeFilter, setActiveFilter] = useState(-1);
  const [filterWorks, setFilterWorks] = useState<Work[]>(currentWorks);
  const intialCount = 3;
  const [perView, setPerView] = useState(intialCount);

  useEffect(() => {
    const items = document.querySelectorAll(".works__item");
    if (items) {
      gsap.fromTo(
        items,
        { opacity: 0, scale: 0 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.5,
          ease: "power1.inOu",
          stagger: 0.2,
        }
      );
    }
  }, [filterWorks]); 

  const filters = useMemo(() => {
    return Array.from(new Set(currentWorks.flatMap((item) => item.categories)));
  }, [currentWorks]);

  const filterHandle = (category: string | null) => {
    if (!category) {
      setActiveFilter(-1);
      setFilterWorks(currentWorks);
    } else {
      setActiveFilter(filters.indexOf(category));
      setFilterWorks(currentWorks.filter((work) => work.categories.includes(category)));
    }
  };
  

  /*useEffect(() => {

    setFilterWorks(activeFilter === -1 ? currentWorks : currentWorks.filter(work => work.categories[activeFilter]));

  }, [i18n.resolvedLanguage, currentWorks, activeFilter]);*/
  
  const showWorks = useMemo(() => filterWorks.slice(0, perView), [filterWorks, perView]);

  const workItems = useRef<HTMLDivElement[]>([]);
  const galleryRef = useRef<HTMLDivElement>(null)

  const showMore = () => {
    const newCount = perView + intialCount;
    setPerView(newCount);


    setTimeout(() => {
      const newItems = workItems.current.slice(perView, newCount);
      gsap.fromTo(
        newItems,
        {
          opacity: 0,
          yPercent: 50,
        },
        {
          opacity: 1,
          yPercent: 0,
          duration: 1,
          stagger: 0.2,
          ease: "power2.out",
        }
      );

      setTimeout(() => {
        const items = document.querySelectorAll('.works__item');
        if (items) {
          items[newCount - 3].scrollIntoView({ behavior: "smooth", block: "end" });
        }
      }, 100);
    }, 0);
  };
  const hide = () => {
    setPerView(intialCount);
    setTimeout(() => {
      if (galleryRef.current) {
        galleryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const filterButtons = useMemo(() => {
    return filters.map((category) => (
      <button
        key={category}
        onClick={() => filterHandle(category)}
        className={`button ${activeFilter === filters.indexOf(category) ? "active" : "button--secondary"}`}
        type="button"
      >
        {category}
      </button>
    ));
  }, [filters, activeFilter]);
  

  return (
    <section className="works" id="works" ref={container}>
      <div className="container h-full">
          <h2 className="works__title app__title center" ref={title}>
            <Trans i18nKey="works_title">My portfolio</Trans>
          </h2>
        <div className="works__filter center">
          <button
            onClick={() => filterHandle(null)}
            className={`button ${activeFilter === -1 ? "active" : "button--secondary"}`}
            type="button"
          >
            {all}
          </button>
          {filterButtons}
        </div>
        <div
          className="works__scroll"
        >
          <BsHandIndex />
        </div>
        <div className="works__gallery" ref={galleryRef}>
          {showWorks.length > 0 ? (
            showWorks.map((item, index) => (
              <div
                className="works__item"
                key={item.title}
                ref={(el) => {
                  if (el) workItems.current[index] = el;
                }}
              >
                <div className="works__item-img">
                  <img
                    src={`${item.imgUrl}`}
                    alt={item.title}
                    width={270}
                    height={185}
                    loading="lazy"
                  />
                  <div className="works__links">
                    <a
                      href={`${item.projectLink}`}
                      target="_blank"
                      rel="noreferrer"
                      className="works__link"
                      title="Live view"
                    >
                      <AiFillEye />
                    </a>
                    <a
                      href={`${item.codeLink}`}
                      target="_blank"
                      rel="noreferrer"
                      className="works__link"
                      title="See legacy code"
                    >
                      <AiFillGithub />
                    </a>
                  </div>
                </div>
                <div className="works__item-text">
                  <ul className="works__tags">
                    {item.tags.map((tag, index) => (
                      <li className="works__tag" key={index}>
                        {tag}
                      </li>
                    ))}
                  </ul>
                  <h3 className="works__item-title">{item.title}</h3>
                  <p className="works__item-description">{item.description}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="works__item-no">No results</p>
          )}
        </div>
        {filterWorks.length > intialCount ? (
          <div className="center works__item-more">
            {filterWorks.length > perView ? (
              <button className="button" onClick={showMore}>
                {t('works_buttons.1')}
              </button>
            ) : (
              <button className="button" onClick={hide}>
                {t('works_buttons.2')}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Works;
